import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, MenuItem,
  Typography, CircularProgress, Box, IconButton, Card, CardContent, Zoom, Fade, Stack,
  Chip
} from "@mui/material"; // MUI 7 এ Grid ডিফল্টভাবে আধুনিক Grid2
import { 
  Add, Remove, Close, WarningAmber,
  CheckCircleOutline, Save, Security, Groups, History
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { createRisk, getRiskTypes } from "../../../../../../../api/services/riskProfile";
import CustomInput from "../../../../../../CustomForm/CustomInput";
import CustomTextArea from "../../../../../../CustomForm/CustomTextArea";
import { QK } from "../../../../../../../base/qk";
import { useParams } from "react-router-dom";

const IMPACT_LEVELS = [
  { value: 1, label: "1: Negligible", desc: "No injury / minor discomfort" },
  { value: 2, label: "2: Minor", desc: "Minor injury / first aid" },
  { value: 3, label: "3: Moderate", desc: "Significant injury / medical treatment" },
  { value: 4, label: "4: Major", desc: "Severe injury / hospitalization" },
  { value: 5, label: "5: Catastrophic", desc: "Death / permanent disability" },
];

const LIKELIHOOD_LEVELS = [
  { value: 1, label: "1: Rare", desc: "Exceptional circumstances" },
  { value: 2, label: "2: Unlikely", desc: "Could occur at some time" },
  { value: 3, label: "3: Possible", desc: "Might occur at some time" },
  { value: 4, label: "4: Likely", desc: "Will probably occur" },
  { value: 5, label: "5: Almost Certain", desc: "Expected to occur" },
];

const initialFormData = {
  risk_type: "",
  risk_name: "",
  risk_area: "",
  initial_impact: 1,
  initial_likelihood: 1,
  latest_impact: 1,
  latest_likelihood: 1,
  target_impact: 1,
  target_likelihood: 1,
  risk_details: "",
  risk_triggers: "",
  who_may_be_harmed: "",
  risk_management: "",
  contingency_required: false,
  contingency_description: "",
  action_required: "",
  contacts: [{ name: "", role: "", contact_details: "" }],
  agreed_by: [{ name: "", role: "", date: "" }],
};

const RiskDialog = ({ open, handleClose, editingRisk }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(initialFormData);
  const { clientID, carePlanID } = useParams();

  const { data: riskTypes } = useQuery([QK.riskTypes], () => getRiskTypes(1, 10, true));

  useEffect(() => {
    if (!open) setFormData(initialFormData);
    else if (editingRisk) setFormData({ ...editingRisk, risk_type: editingRisk.risk_type?.id || "" });
  }, [open, editingRisk]);

  const mutation = useMutation(createRisk, {
    onSuccess: () => {
      enqueueSnackbar(`Risk profile updated`, { variant: "success" });
      queryClient.invalidateQueries(QK.risks);
      handleClose();
    },
    onError: () => enqueueSnackbar(`Error saving record`, { variant: "error" }),
  });

  const handleChange = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  const handleArrayChange = (field, index, key, value) => {
    const updated = formData[field].map((item, i) => i === index ? { ...item, [key]: value } : item);
    setFormData(prev => ({ ...prev, [field]: updated }));
  };

  const handleAddRow = (field, template) => setFormData(prev => ({ ...prev, [field]: [...prev[field], { ...template }] }));

  const handleRemoveRow = (field, index) => {
    if (formData[field].length <= 1) return;
    setFormData(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  const getRiskLevel = (impact, likelihood) => {
    const score = impact * likelihood;
    if (score >= 15) return { label: "EXTREME", color: "#d32f2f", bg: "#fff5f5", shadow: "0 4px 10px #d32f2f20" };
    if (score >= 8) return { label: "HIGH", color: "#ed6c02", bg: "#fff9f2", shadow: "0 4px 10px #ed6c0220" };
    if (score >= 4) return { label: "MEDIUM", color: "#0288d1", bg: "#f0faff", shadow: "0 4px 10px #0288d120" };
    return { label: "LOW", color: "#2e7d32", bg: "#f6ffed", shadow: "0 4px 10px #2e7d3220" };
  };

  const RiskAssessmentCard = ({ title, prefix, icon: Icon }) => {
    const impact = formData[`${prefix}_impact`];
    const likelihood = formData[`${prefix}_likelihood`];
    const risk = getRiskLevel(impact, likelihood);
    
    return (
      <Card variant="outlined" sx={{ 
        height: '100%',
        transition: 'all 0.3s ease',
        boxShadow: risk.shadow,
        border: `1px solid ${risk.color}40`,
        bgcolor: risk.bg,
        borderRadius: 2
      }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", display: 'flex', alignItems: 'center', gap: 0.5, textTransform: 'uppercase' }}>
              <Icon sx={{ fontSize: 16 }} /> {title}
            </Typography>
            <Chip
              label={`${risk.label} ${impact * likelihood}`} 
              size="small"
              sx={{ fontWeight: 900, bgcolor: risk.color, color: '#fff', fontSize: '0.65rem', height: 20 }} 
            />
          </Stack>
          <Grid container spacing={1}>
            <Grid size={12}>
              <CustomInput select label="Impact" size="small" fullWidth value={impact} onChange={(e) => handleChange(`${prefix}_impact`, e.target.value)}>
                {IMPACT_LEVELS.map(l => (
                  <MenuItem key={l.value} value={l.value}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{l.label}</Typography>
                  </MenuItem>
                ))}
              </CustomInput>
            </Grid>
            <Grid size={12}>
              <CustomInput select label="Likelihood" size="small" fullWidth value={likelihood} onChange={(e) => handleChange(`${prefix}_likelihood`, e.target.value)}>
                {LIKELIHOOD_LEVELS.map(l => (
                  <MenuItem key={l.value} value={l.value}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{l.label}</Typography>
                  </MenuItem>
                ))}
              </CustomInput>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth 
      TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' } }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(to right, #f8f9fa, #ffffff)', 
        borderBottom: "1px solid #edf2f7",
        py: 2
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "primary.dark" }}>
              {editingRisk ? "Revise Risk Assessment" : "Clinical Risk Entry"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>Ensure all clinical hazards are documented accurately.</Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ bgcolor: '#f5f5f5' }}><Close fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        <Box>
          {/* Section 1: Foundation */}
          <Typography variant="overline" sx={{ color: "primary.main", fontWeight: 700, mb: 1, display: 'block' }}>1. Basic Information</Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomInput label="Identification Name" placeholder="e.g. Mobility Risk" fullWidth value={formData.risk_name} onChange={(e) => handleChange("risk_name", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomInput select label="Domain/Area" fullWidth value={formData.risk_area} onChange={(e) => handleChange("risk_area", e.target.value)}>
                <MenuItem value="care_plan">Care Plan</MenuItem>
                <MenuItem value="initial_risk_assessment">Initial Risk Assessment</MenuItem>
              </CustomInput>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomInput select label="Hazard Category" fullWidth value={formData.risk_type} onChange={(e) => handleChange("risk_type", e.target.value)}>
                {riskTypes?.results?.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </CustomInput>
            </Grid>
          </Grid>

          {/* Section 2: Clinical Matrix */}
          <Typography variant="overline" sx={{ color: "primary.main", fontWeight: 700, mb: 1, display: 'block' }}>2. Scoring Matrix (5x5)</Typography>
          
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 4 }}><RiskAssessmentCard title="Initial Level" prefix="initial" icon={Security} /></Grid>
            <Grid size={{ xs: 12, md: 4 }}><RiskAssessmentCard title="Latest Level" prefix="latest" icon={History} /></Grid>
            <Grid size={{ xs: 12, md: 4 }}><RiskAssessmentCard title="Target Level" prefix="target" icon={CheckCircleOutline} /></Grid>
          </Grid>

          {/* Section 3: Narratives */}
          <Typography variant="overline" sx={{ color: "primary.main", fontWeight: 700, mb: 1, display: 'block' }}>3. Hazard Analysis</Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={12}><CustomTextArea label="Risk Narrative & Details" placeholder="Describe the hazard in detail..." rows={3} fullWidth value={formData.risk_details} onChange={(e) => handleChange("risk_details", e.target.value)} /></Grid>
            <Grid size={{ xs: 12, md: 6 }}><CustomTextArea label="Triggers & Warning Signs" placeholder="What leads to this event?" rows={2} fullWidth value={formData.risk_triggers} onChange={(e) => handleChange("risk_triggers", e.target.value)} /></Grid>
            <Grid size={{ xs: 12, md: 6 }}><CustomTextArea label="At-Risk Individuals" placeholder="Who is likely to be harmed?" rows={2} fullWidth value={formData.who_may_be_harmed} onChange={(e) => handleChange("who_may_be_harmed", e.target.value)} /></Grid>
            <Grid size={12}><CustomTextArea label="Mitigation & Management Plan" placeholder="How are we reducing this risk?" rows={3} fullWidth value={formData.risk_management} onChange={(e) => handleChange("risk_management", e.target.value)} /></Grid>
          </Grid>

          {/* Section 4: Safety Safeguards */}
          <Box sx={{ p: 3, bgcolor: formData.contingency_required ? "#fffcf0" : "#f8fafc", borderRadius: 3, border: "1px dashed #cbd5e1", mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <WarningAmber color="warning" />
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Emergency Contingency</Typography>
            </Stack>
            <CustomInput select label="Emergency Plan Triggered?" fullWidth value={formData.contingency_required ? "yes" : "no"} onChange={(e) => handleChange("contingency_required", e.target.value === "yes")}>
              <MenuItem value="yes">Yes - Specific action required for safety</MenuItem>
              <MenuItem value="no">No - Managed by routine care</MenuItem>
            </CustomInput>
            {formData.contingency_required && (
              <Zoom in={formData.contingency_required}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={12}><CustomTextArea label="Triggering Event Description" fullWidth value={formData.contingency_description} onChange={(e) => handleChange("contingency_description", e.target.value)} /></Grid>
                  <Grid size={12}><CustomTextArea label="Clinical Response Required" fullWidth value={formData.action_required} onChange={(e) => handleChange("action_required", e.target.value)} /></Grid>
                </Grid>
              </Zoom>
            )}
          </Box>

          {/* Section 5: Accountability */}
          <Grid container spacing={4}>
             <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Groups fontSize="small" /> Key Stakeholders
                </Typography>
                {formData.contacts.map((c, i) => (
                  <Stack key={i} direction="row" spacing={1} sx={{ mb: 2 }} alignItems="flex-start">
                    <Box sx={{ flexGrow: 1, p: 2, border: '1px solid #edf2f7', borderRadius: 2 }}>
                       <Grid container spacing={1}>
                          <Grid size={6}><CustomInput label="Name" size="small" fullWidth value={c.name} onChange={(e) => handleArrayChange("contacts", i, "name", e.target.value)} /></Grid>
                          <Grid size={6}><CustomInput label="Role" size="small" fullWidth value={c.role} onChange={(e) => handleArrayChange("contacts", i, "role", e.target.value)} /></Grid>
                          <Grid size={12}><CustomInput label="Contact Details" size="small" fullWidth value={c.contact_details} onChange={(e) => handleArrayChange("contacts", i, "contact_details", e.target.value)} /></Grid>
                       </Grid>
                    </Box>
                    <IconButton size="small" onClick={() => handleRemoveRow("contacts", i)} sx={{ mt: 1 }}><Remove fontSize="small" /></IconButton>
                  </Stack>
                ))}
                <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => handleAddRow("contacts", { name: "", role: "", contact_details: "" })}>Add Stakeholder</Button>
             </Grid>

             <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleOutline fontSize="small" /> Sign-off / Agreement
                </Typography>
                {formData.agreed_by.map((a, i) => (
                  <Stack key={i} direction="row" spacing={1} sx={{ mb: 2 }} alignItems="flex-start">
                    <Box sx={{ flexGrow: 1, p: 2, border: '1px solid #edf2f7', borderRadius: 2 }}>
                       <Grid container spacing={1}>
                          <Grid size={6}><CustomInput label="Signatory" size="small" fullWidth value={a.name} onChange={(e) => handleArrayChange("agreed_by", i, "name", e.target.value)} /></Grid>
                          <Grid size={6}><CustomInput label="Designation" size="small" fullWidth value={a.role} onChange={(e) => handleArrayChange("agreed_by", i, "role", e.target.value)} /></Grid>
                          <Grid size={12}><CustomInput label="Date" type="date" size="small" fullWidth value={a.date} onChange={(e) => handleArrayChange("agreed_by", i, "date", e.target.value)} slotProps={{ inputLabel: { shrink: true } }} /></Grid>
                       </Grid>
                    </Box>
                    <IconButton size="small" onClick={() => handleRemoveRow("agreed_by", i)} sx={{ mt: 1 }}><Remove fontSize="small" /></IconButton>
                  </Stack>
                ))}
                <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => handleAddRow("agreed_by", { name: "", role: "", date: "" })}>Add Signatory</Button>
             </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #edf2f7' }}>
        <Button onClick={handleClose} sx={{ fontWeight: 700, color: 'text.secondary' }}>Discard</Button>
        <Button 
          variant="contained" 
          startIcon={mutation.isLoading ? <CircularProgress size={18} color="inherit" /> : <Save />}
          onClick={() => mutation.mutate({ ...formData, client: clientID, risk_type_id: formData.risk_type, care_plan: carePlanID })} 
          disabled={mutation.isLoading}
          sx={{ 
            px: 4, 
            py: 1.2, 
            borderRadius: 2, 
            fontWeight: 800,
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            textTransform: 'none'
          }}
        >
          Finalize Assessment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RiskDialog;